import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "afm_cookie_consent";

/**
 * CookieConsentBanner — UK PECR / GDPR-friendly cookie consent.
 *
 * Only shows on first visit. User can accept (all cookies) or accept essentials-only.
 * Choice is persisted via the same key documented in /cookies.
 *
 * We don't currently set any non-essential cookies, so "accept all" and "essentials only"
 * have the same effect today — but the banner is in place so we're compliant when we add
 * analytics later.
 */
const CookieConsentBanner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const choice = localStorage.getItem(STORAGE_KEY);
      if (!choice) setShow(true);
    } catch { /* SSR / privacy modes */ }
  }, []);

  const dismiss = (mode) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, ts: Date.now() }));
    } catch { /* ignore */ }
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 inset-x-0 z-50 px-4 sm:px-6 pb-4 sm:pb-6 pointer-events-none"
          data-testid="cookie-consent-banner"
        >
          <div className="container-narrow pointer-events-auto">
            <div className="bg-white/95 backdrop-blur-md border border-black/10 shadow-2xl rounded-lg p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="shrink-0 w-10 h-10 rounded-full bg-[#F3EFE9] flex items-center justify-center">
                  <Cookie className="w-5 h-5 text-[#C05A3C]" />
                </div>
                <div className="text-sm leading-snug">
                  <div className="font-semibold text-[#1A1A1A] mb-1">We use a few cookies</div>
                  <p className="text-muted-foreground">
                    Just the essentials — to keep you signed in, remember your currency, and protect against
                    bots. No tracking. No ads.{" "}
                    <Link to="/cookies" className="text-[#C05A3C] underline">Read more</Link>.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => dismiss("essentials")}
                  data-testid="cookie-essentials-btn"
                  className="border-[#2A3D36] text-[#2A3D36] hover:bg-[#2A3D36]/5 h-9"
                >
                  Essentials only
                </Button>
                <Button
                  size="sm"
                  onClick={() => dismiss("all")}
                  data-testid="cookie-accept-all-btn"
                  className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white h-9"
                >
                  Got it
                </Button>
                <button
                  type="button"
                  onClick={() => dismiss("essentials")}
                  aria-label="Dismiss cookie banner"
                  className="sm:hidden p-1.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
